// components/TicketDetail.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppServices } from "../../../hook/services";
import { useSocket } from "../../../context/socket";

const STATUS_CONFIG = {
  Pending:       { color: "#fd7e14", bg: "#ffe9d6", icon: "bi-arrow-repeat" },
  Open:          { color: "#0d6efd", bg: "#dbeafe", icon: "bi-circle-fill" },
  "In Progress": { color: "#fd7e14", bg: "#ffe9d6", icon: "bi-arrow-repeat" },
  Closed:        { color: "#198754", bg: "#dcf5e7", icon: "bi-check-circle-fill" },
  Resolved:      { color: "#6c757d", bg: "#e2e3e5", icon: "bi-check-circle-fill" },
};

const PRIORITY_CONFIG = {
  High:   { color: "#dc3545", bg: "#fde8ea", icon: "bi-exclamation-triangle-fill" },
  Medium: { color: "#b8860b", bg: "#fff6d6", icon: "bi-exclamation-circle-fill" },
  Low:    { color: "#198754", bg: "#dcf5e7", icon: "bi-info-circle-fill" },
};

const initials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso.split("T")[0];
  }
};

const StatusPill = ({ value, cfg }) => {
  if (!cfg) return null;
  return (
    <span
      className="d-inline-flex align-items-center gap-2 fw-semibold"
      style={{
        background: cfg.bg,
        color: cfg.color,
        padding: "6px 12px",
        borderRadius: 999,
        fontSize: "0.78rem",
        border: `1px solid ${cfg.color}25`,
      }}
    >
      <i className={`bi ${cfg.icon}`} style={{ fontSize: "0.7rem" }}></i>
      {value}
    </span>
  );
};

const Avatar = ({ text, isMine }) => (
  <div
    className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
    style={{
      width: 38,
      height: 38,
      fontSize: "0.78rem",
      background: isMine
        ? "linear-gradient(135deg, #6ea8fe, #0d6efd)"
        : "linear-gradient(135deg, #495057, #212529)",
      color: "#fff",
      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
    }}
  >
    {text || "?"}
  </div>
);

const InfoRow = ({ icon, label, value, color, truncate = false }) => (
  <div className="d-flex align-items-center justify-content-between py-2">
    <span
      className="d-inline-flex align-items-center gap-2 text-muted"
      style={{ fontSize: "0.85rem" }}
    >
      <i className={`bi ${icon}`} style={{ color: color || "#6c757d" }}></i>
      {label}
    </span>
    <span
      className={`fw-semibold ${truncate ? "text-truncate" : ""}`}
      style={{ maxWidth: 170, fontSize: "0.9rem" }}
      title={value}
    >
      {value || "—"}
    </span>
  </div>
);

const MessageBubble = React.memo(({ reply, currentUserName }) => {
  // In this customer-facing view: "User" = me (right-aligned blue), "Support Agent" = them (left-aligned dark)
  const isMine = reply.sender === "User" || reply.user === "User";
  const isSupport = reply.sender === "Support Agent" || reply.user === "Support Agent";
  const senderName =
    reply.userName ||
    reply.user ||
    (isMine ? currentUserName || "You" : isSupport ? "Support Agent" : "Customer");

  return (
    <div
      className={`d-flex gap-2 mb-3 align-items-end ${isMine ? "flex-row-reverse" : ""}`}
      style={{ animation: "msg-in 0.25s ease-out" }}
    >
      <Avatar text={initials(senderName)} isMine={isMine} />
      <div style={{ maxWidth: "75%" }}>
        <div
          className={`d-flex align-items-center gap-2 mb-1 small ${
            isMine ? "justify-content-end" : ""
          }`}
          style={{ color: "#6c757d" }}
        >
          <span className="fw-semibold" style={{ color: "#212529" }}>
            {senderName}
          </span>
          <span style={{ fontSize: "0.72rem" }}>
            {formatDate(reply.createdAt)}
          </span>
        </div>
        <div
          style={{
            background: isMine
              ? "linear-gradient(135deg, #0d6efd, #0a58ca)"
              : "#f1f3f5",
            color: isMine ? "#fff" : "#212529",
            padding: "12px 16px",
            borderRadius: 14,
            borderTopRightRadius: isMine ? 4 : 14,
            borderTopLeftRadius: isMine ? 14 : 4,
            boxShadow: isMine
              ? "0 2px 8px rgba(13,110,253,0.25)"
              : "0 1px 3px rgba(0,0,0,0.06)",
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {reply.text}
        </div>
        {isSupport && (
          <div
            className="d-flex align-items-center gap-1 mt-1"
            style={{ fontSize: "0.7rem", color: "#9ca3af" }}
          >
            <i className="bi bi-headset" style={{ color: "#495057" }}></i>
            Support Team Response
          </div>
        )}
        {isMine && (
          <div
            className="d-flex justify-content-end align-items-center gap-1 mt-1"
            style={{ fontSize: "0.7rem", color: "#9ca3af" }}
          >
            <i className="bi bi-check2-all" style={{ color: "#0d6efd" }}></i>
            Sent
          </div>
        )}
      </div>
    </div>
  );
});

const TicketDetail = () => {
  const socket = useSocket();
  const navigate = useNavigate();
  const AppService = useAppServices();
  const { ticketId, companyId, locationId, userId, email, userName } = useParams();
  const [ticket, setTicket] = useState({});
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [loading, setLoading] = useState(true);

  const threadRef = useRef(null);
  const textareaRef = useRef(null);

  const status = STATUS_CONFIG[ticket?.status];
  const priority = PRIORITY_CONFIG[ticket?.priority];

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleMessageResponse = (data) => {
      setMessages((prev) => [...prev, data]);
    };

    const handleTicketStatus = ({ _id, status }) => {
      if (ticket?._id === _id) {
        setTicket((prev) => ({ ...prev, status }));
      }
    };

    const handleTicketPriority = ({ _id, priority }) => {
      if (ticket?._id === _id) {
        setTicket((prev) => ({ ...prev, priority }));
      }
    };

    socket.on("messageResponse", handleMessageResponse);
    socket.on("ticketStatusUpdated", handleTicketStatus);
    socket.on("ticketPriorityUpdated", handleTicketPriority);

    return () => {
      socket.off("messageResponse", handleMessageResponse);
      socket.off("ticketStatusUpdated", handleTicketStatus);
      socket.off("ticketPriorityUpdated", handleTicketPriority);
    };
  }, [socket, ticket]);

  const getTicket = async () => {
    try {
      setLoading(true);
      const { response } =
        await AppService.support.get_single_ticket_with_messages({
          query: `_id=${ticketId}`,
        });
      if (response) {
        setTicket(response.data);
        setMessages(response.messages);
      } else {
        setTicket({});
        setMessages([]);
      }
    } catch (err) {
      console.error("Error fetching tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTicket();
  }, []);

  // Auto-scroll on new message
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages?.length]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, [replyText]);

  const handleSubmitReply = (e) => {
    e?.preventDefault?.();
    if (!replyText.trim()) return;

    // Prefer the ticket's stored customer details (this is the customer
    // replying to their own ticket); fall back to URL params only if missing.
    const senderUserId = userId;
    const senderEmail =  email;
    const senderUserName = userName;

    socket?.emit("sendMessage", {
      companyId,
      locationId,
      userId: senderUserId,
      email: senderEmail,
      userName: senderUserName,
      user: {
        userId: senderUserId,
        email: senderEmail,
        userName: senderUserName,
      },
      ticket_id: ticket.ticket_id,
      ticketRefId: ticket._id,
      sender: "User",
      text: replyText.trim(),
      id: `${socket.id}${Math.random()}`,
      socketID: socket.id,
    });
    setReplyText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmitReply();
    }
  };

  const charCount = replyText.length;
  const charLimit = 2000;
  const heroAccent = priority?.color || status?.color || "#0d6efd";

  return (
    <div style={{ animation: "detail-in 0.3s ease-out" }}>
      <style>{`
        @keyframes msg-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes detail-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .ticket-hero {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fb 100%);
          border: 1px solid #e9ecef;
          border-radius: 14px;
          overflow: hidden;
        }
        .ticket-hero-bar { height: 4px; }
        .info-card, .convo-card {
          border: 1px solid #e9ecef;
          border-radius: 14px;
          background: #fff;
          overflow: hidden;
        }
        .convo-card-header {
          background: #fafbfc;
          border-bottom: 1px solid #e9ecef;
          padding: 14px 18px;
        }
        .reply-composer {
          background: #fafbfc;
          border-top: 1px solid #e9ecef;
          padding: 14px 18px;
        }
        .reply-textarea {
          width: 100%;
          border: 1px solid #dee2e6;
          border-radius: 10px;
          padding: 10px 12px;
          resize: none;
          min-height: 44px;
          max-height: 160px;
          font-size: 0.92rem;
          line-height: 1.5;
          transition: border-color 0.15s, box-shadow 0.15s;
          background: #fff;
        }
        .reply-textarea:focus {
          outline: none;
          border-color: #0d6efd;
          box-shadow: 0 0 0 3px rgba(13,110,253,0.15);
        }
        .send-btn {
          background: linear-gradient(135deg, #0d6efd, #0a58ca);
          color: #fff;
          border: none;
          padding: 8px 18px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.88rem;
          transition: transform 0.15s, box-shadow 0.15s, opacity 0.15s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .send-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(13,110,253,0.3);
        }
        .send-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .icon-btn {
          background: #fff;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 6px 12px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #495057;
          transition: all 0.15s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .icon-btn:hover { background: #f1f3f5; border-color: #adb5bd; }
        .convo-thread::-webkit-scrollbar { width: 6px; }
        .convo-thread::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 6px; }
        .convo-thread::-webkit-scrollbar-thumb:hover { background: #adb5bd; }
        .skeleton {
          background: linear-gradient(90deg, #eef0f2 25%, #f6f7f9 50%, #eef0f2 75%);
          background-size: 800px 100%;
          animation: shimmer 1.4s infinite linear;
          border-radius: 6px;
        }
      `}</style>

      {/* Top action bar */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <button className="icon-btn" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left"></i>
          Back to Tickets
        </button>
        <button className="icon-btn" onClick={getTicket} disabled={loading}>
          <i className="bi bi-arrow-clockwise"></i>
          Refresh
        </button>
      </div>

      {/* Hero header */}
      <div className="ticket-hero mb-4">
        <div className="ticket-hero-bar" style={{ background: heroAccent }}></div>
        <div className="p-4">
          {loading && !ticket?._id ? (
            <>
              <div className="skeleton mb-2" style={{ height: 14, width: 220 }}></div>
              <div className="skeleton" style={{ height: 24, width: "70%" }}></div>
            </>
          ) : (
            <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div
                  className="d-flex align-items-center gap-2 mb-2 text-muted flex-wrap"
                  style={{ fontSize: "0.82rem" }}
                >
                  <span className="fw-semibold" style={{ color: "#495057" }}>
                    {ticket.ticket_id || ticket._id}
                  </span>
                  {ticket.updatedAt && (
                    <>
                      <span style={{ opacity: 0.5 }}>•</span>
                      <span>
                        <i className="bi bi-clock me-1"></i>
                        Updated {formatDate(ticket.updatedAt)}
                      </span>
                    </>
                  )}
                </div>
                <h4 className="fw-bold mb-0" style={{ color: "#212529", lineHeight: 1.3 }}>
                  {ticket.subject || "Untitled ticket"}
                </h4>
              </div>
              <div className="d-flex gap-2 flex-shrink-0 flex-wrap">
                {ticket.status && <StatusPill value={ticket.status} cfg={status} />}
                {ticket.priority && (
                  <StatusPill value={`${ticket.priority} Priority`} cfg={priority} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="row g-3">
        {/* Left Column - Ticket Information */}
        <div className="col-lg-4">
          <div className="info-card">
            <div
              className="convo-card-header d-flex align-items-center gap-2"
              style={{ fontWeight: 600 }}
            >
              <i className="bi bi-info-circle" style={{ color: "#495057" }}></i>
              Ticket Information
            </div>
            <div className="p-3">
              <div className="mb-3">
                <div
                  className="text-muted mb-1"
                  style={{
                    fontSize: "0.78rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                  }}
                >
                  Description
                </div>
                {loading && !ticket?._id ? (
                  <>
                    <div className="skeleton mb-2" style={{ height: 12 }}></div>
                    <div className="skeleton mb-2" style={{ height: 12, width: "85%" }}></div>
                    <div className="skeleton" style={{ height: 12, width: "60%" }}></div>
                  </>
                ) : (
                  <div
                    style={{
                      background: "#f8f9fa",
                      border: "1px solid #f1f3f5",
                      borderRadius: 10,
                      padding: 12,
                      fontSize: "0.9rem",
                      lineHeight: 1.55,
                      color: "#343a40",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {ticket.description || "No description provided."}
                  </div>
                )}
              </div>

              <div style={{ borderTop: "1px solid #f1f3f5" }} className="pt-2">
                <InfoRow
                  icon="bi-person-circle"
                  label="Name"
                  value={ticket.userName}
                  color="#0d6efd"
                  truncate
                />
                <InfoRow
                  icon="bi-envelope"
                  label="Email"
                  value={ticket.email}
                  color="#6610f2"
                  truncate
                />
                <InfoRow
                  icon="bi-calendar-plus"
                  label="Created"
                  value={formatDate(ticket.createdAt)}
                  color="#198754"
                />
                <InfoRow
                  icon="bi-arrow-clockwise"
                  label="Last Updated"
                  value={formatDate(ticket.updatedAt)}
                  color="#6c757d"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Conversation */}
        <div className="col-lg-8">
          <div className="convo-card d-flex flex-column" style={{ minHeight: 620 }}>
            <div className="convo-card-header d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2 fw-semibold">
                <i className="bi bi-chat-left-text" style={{ color: "#495057" }}></i>
                Conversation
                <span
                  className="badge"
                  style={{
                    background: "#e9ecef",
                    color: "#495057",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }}
                >
                  {messages?.length || 0}
                </span>
              </div>
              <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                <i className="bi bi-shield-check me-1" style={{ color: "#198754" }}></i>
                Secure thread
              </span>
            </div>

            {/* Conversation thread */}
            <div
              ref={threadRef}
              className="convo-thread flex-grow-1"
              style={{
                overflowY: "auto",
                padding: "20px 18px",
                maxHeight: 480,
                background: "linear-gradient(180deg, #fbfbfc 0%, #ffffff 100%)",
              }}
            >
              {loading && !messages?.length ? (
                <div className="d-flex flex-column gap-3">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className={`d-flex gap-2 ${i % 2 === 0 ? "" : "flex-row-reverse"}`}
                    >
                      <div
                        className="skeleton rounded-circle"
                        style={{ width: 38, height: 38 }}
                      ></div>
                      <div
                        className="skeleton"
                        style={{ height: 60, width: "55%", borderRadius: 12 }}
                      ></div>
                    </div>
                  ))}
                </div>
              ) : messages?.length === 0 ? (
                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                    style={{
                      width: 64,
                      height: 64,
                      background: "#f1f3f5",
                      color: "#adb5bd",
                    }}
                  >
                    <i
                      className="bi bi-chat-left-text"
                      style={{ fontSize: "1.6rem" }}
                    ></i>
                  </div>
                  <h6 className="fw-semibold mb-1" style={{ color: "#495057" }}>
                    No messages yet
                  </h6>
                  <p className="text-muted small mb-0">
                    Start the conversation by sending the first reply below.
                  </p>
                </div>
              ) : (
                messages.map((reply) => (
                  <MessageBubble
                    key={reply._id || reply.id}
                    reply={reply}
                    currentUserName={ticket.userName || userName}
                  />
                ))
              )}
            </div>

            {/* Reply composer */}
            <div className="reply-composer mt-auto">
              <form onSubmit={handleSubmitReply}>
                <div className="d-flex gap-2 align-items-end">
                  <Avatar
                    text={initials(ticket.userName || userName) || "U"}
                    isMine
                  />
                  <div className="flex-grow-1">
                    <textarea
                      ref={textareaRef}
                      className="reply-textarea"
                      value={replyText}
                      maxLength={charLimit}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type your reply… (Enter to send, Shift+Enter for new line)"
                      rows={1}
                    />
                    <div className="d-flex align-items-center justify-content-between mt-2">
                      <div className="text-muted small d-flex align-items-center gap-3">
                        <span>
                          <i className="bi bi-info-circle me-1"></i>
                          Enter to send · Shift+Enter for new line
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className="small"
                          style={{
                            color:
                              charCount > charLimit * 0.9
                                ? "#dc3545"
                                : "#adb5bd",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {charCount}/{charLimit}
                        </span>
                        <button
                          type="submit"
                          className="send-btn"
                          disabled={!replyText.trim()}
                        >
                          <i className="bi bi-send-fill"></i>
                          Send
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
